import axios, { AxiosError } from 'axios';
import secureAxios, { handleSecurityError } from './security';
import { handleError, AppError, ERROR_CODES } from '../utils/errorHandler';

const devHost =
  typeof window !== 'undefined' && window.location?.hostname
    ? window.location.hostname
    : 'localhost';

const API_URL =
  process.env.NODE_ENV === 'production'
    ? 'https://app.shorekart.com/api'
    : process.env.REACT_APP_API_URL || `http://${devHost}:3001/api`;

// Configure base URL
secureAxios.defaults.baseURL = API_URL;

// Set timeout for all requests (especially important for AI analysis)
secureAxios.defaults.timeout = 120000; // 2 minutes timeout for AI operations

// Request interceptor to add auth token
secureAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    console.log('Request interceptor - Token from localStorage:', token ? 'Token exists' : 'No token');
    console.log('Request interceptor - URL:', config.url);
    console.log('Request interceptor - Method:', config.method);
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Request interceptor - Authorization header set:', `Bearer ${token.substring(0, 20)}...`);
    } else {
      console.log('Request interceptor - No token found, skipping Authorization header');
    }
    
    console.log('Request interceptor - Final headers:', config.headers);
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(
      new AppError(
        'Failed to process request',
        ERROR_CODES.NETWORK_ERROR,
        500,
        { originalError: error },
        false
      )
    );
  }
);

// Response interceptor for error handling
secureAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      
      // Handle 401 errors by redirecting to login
      if (status === 401) {
        const hadToken = Boolean(localStorage.getItem('token'));

        // Clear expired tokens
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        
        // Redirect only when a session existed; this avoids forcing login from public pages.
        if (hadToken && window.location.pathname !== '/login' && window.location.pathname !== '/smartfarm/login') {
          window.location.href = '/smartfarm/login';
        }
        
        return Promise.reject(
          new AppError(
            hadToken ? 'Session expired. Please log in again.' : (data?.message || 'Unauthorized'),
            ERROR_CODES.UNAUTHORIZED,
            401,
            { requiresAuth: hadToken }
          )
        );
      }
      
      switch (status) {
        case 403:
          return Promise.reject(
            new AppError(
              data?.message || 'Forbidden',
              ERROR_CODES.FORBIDDEN,
              403,
              { requiresAuth: true }
            )
          );
        case 404:
          return Promise.reject(
            new AppError(
              data?.message || 'Resource not found',
              ERROR_CODES.NOT_FOUND,
              404
            )
          );
        case 429:
          return Promise.reject(
            new AppError(
              'Too many requests',
              ERROR_CODES.RATE_LIMIT_EXCEEDED,
              429,
              { retryAfter: error.response?.headers?.['retry-after'] }
            )
          );
        case 500:
          return Promise.reject(
            new AppError(
              'Internal server error',
              ERROR_CODES.INTERNAL_SERVER_ERROR,
              500
            )
          );
        default:
          return Promise.reject(
            new AppError(
              data?.message || 'Request failed',
              `HTTP_${status}`,
              status,
              { response: data }
            )
          );
      }
    }
    
    return Promise.reject(error);
  }
);

export default secureAxios;
